/**
 * Categories a reviewer can attribute a wrong AI analysis to, with the
 * description and example list surfaced in each row's info tooltip.
 *
 * @module components/questionnaire/feedbackCategories
 */

/** One selectable feedback category plus its tooltip copy. */
export interface FeedbackCategory {
  /** Value stored on the questionnaire's `user_feedback.selected_option`. */
  value: string;
  label: string;
  /** What the category means, shown under "Description" on hover. */
  description: string;
  /** Concrete failure modes the category covers, shown under "Includes". */
  includes: string;
}

/**
 * Static category list. Kept module-level so its identity is stable across
 * renders.
 */
export const FEEDBACK_CATEGORIES: FeedbackCategory[] = [
  {
    value: "clinical-interpretation",
    label: "Clinical Interpretation",
    description:
      "The bot misunderstood or incorrectly interpreted the patient's clinical information.",
    includes:
      "Wrong Diagnosis, Wrong Biomarker Status, Wrong Continuation vs New Start, Wrong Concomitant Therapy, Wrong Treatment Outcome, Wrong Age Validation, Wrong Gender, Weight/BMI Miscalculated, Missing Comorbidity, Missing Biomarker, Prior Treatment Missed, Allergy/Contraindication Missed, Wrong Dosing, Missing Dosage",
  },
  {
    value: "evidence-review",
    label: "Missing Documentation / Evidence Review",
    description:
      "The bot missed, ignored, or used incorrect clinical evidence while answering.",
    includes:
      "Missing Clinical Document (CMM), Missing Complete Clinical Document, Missing Partial Clinical Document, Bot Picked Old MD Note, Labs Not Referenced, Missing Clinical Justification",
  },
  {
    value: "guideline-coverage-criteria",
    label: "Guideline / Coverage Criteria",
    description:
      "The bot incorrectly applied payer, FDA, or drug-specific coverage criteria.",
    includes:
      "Generic vs Brand, Wrong Default Applied, Missed Tried & Failed, Preferred / Non-Preferred, Guideline Interpretation, Drug-Specific Coverage Rules",
  },
  {
    value: "reasoning-answer-selection",
    label: "Reasoning / Answer Selection",
    description:
      "The bot had the available information but arrived at the wrong conclusion or selected the wrong answer.",
    includes:
      "Contradiction in Answer, Empty Clinical Answer, Bot Didn't Answer, Timeline Miscalculated, Wrong Logical Conclusion",
  },
  {
    value: "system-data-issue",
    label: "System / Data Issue",
    description:
      "The issue was caused by missing data, dashboard behavior, or system limitations rather than AI analysis.",
    includes:
      "Dashboard Didn't Pick Questionnaire, Dashboard Picked Partial Questionnaire, Dashboard Mapping Issue, Manual Done",
  },
];
